import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuthApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/PageHeader';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/components/ui/use-toast';
import demoStudioService from '@/lib/api/demoStudio';
import contentLibraryService from '@/lib/api/contentLibrary';
import { getBrowserTimezone } from '@/lib/timezone';
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Clapperboard,
  Clock,
  File as FileIcon,
  ListChecks,
  Loader2,
  MonitorPlay,
  Play,
  RotateCw,
  Trash2,
  Upload,
  Video,
  X,
  XCircle,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const buildDefaultRecordingName = (organizationName?: string | null) =>
  organizationName ? `${organizationName} demo capture` : 'Demo capture';

const formatSessionTime = (value?: string | null) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const RECORDINGS_PAGE_SIZE = 12;

type SegmentLifecycleCounts = {
  active: number;
  staged: number;
  library: number;
  discard: number;
  total: number;
  suggestedAdd: number;
  possibleReplacements: number;
  possibleDuplicates: number;
  suggestedLibrary: number;
  suggestedDiscard: number;
};

const createEmptySegmentCounts = (): SegmentLifecycleCounts => ({
  active: 0,
  staged: 0,
  library: 0,
  discard: 0,
  total: 0,
  suggestedAdd: 0,
  possibleReplacements: 0,
  possibleDuplicates: 0,
  suggestedLibrary: 0,
  suggestedDiscard: 0,
});

const getRecommendationKind = (action?: string | null) => {
  switch (action) {
    case 'new_candidate':
    case 'approve':
    case 'activate':
      return 'add';
    case 'possible_replacement':
    case 'replace_matched':
      return 'replacement';
    case 'possible_duplicate':
      return 'duplicate';
    case 'library_only_candidate':
    case 'library_only':
      return 'library';
    case 'ignore':
      return 'discard';
    default:
      return 'unknown';
  }
};

const formatCount = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const formatReadySummary = (counts: SegmentLifecycleCounts) => {
  const parts = [];
  if (counts.suggestedAdd > 0) parts.push(`${counts.suggestedAdd} suggested add`);
  if (counts.possibleReplacements > 0) {
    parts.push(`${counts.possibleReplacements} possible replacement${counts.possibleReplacements === 1 ? '' : 's'}`);
  }
  if (counts.possibleDuplicates > 0) {
    parts.push(`${counts.possibleDuplicates} possible duplicate${counts.possibleDuplicates === 1 ? '' : 's'}`);
  }
  if (counts.suggestedLibrary > 0) parts.push(`${counts.suggestedLibrary} suggested library`);
  if (counts.suggestedDiscard > 0) parts.push(`${counts.suggestedDiscard} suggested discard`);
  return parts.length ? parts.join(' · ') : 'Awaiting decisions in Agent Assets';
};

const formatReviewedSummary = (counts: SegmentLifecycleCounts) => {
  if (counts.total === 0) return 'No agent assets generated';
  const parts = [];
  if (counts.active > 0) parts.push(`${counts.active} active`);
  if (counts.library > 0) parts.push(`${counts.library} library`);
  if (counts.discard > 0) parts.push(`${counts.discard} discarded`);
  return parts.length ? parts.join(' · ') : formatCount(counts.total, 'segment');
};

const STATUS_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  complete: { icon: CheckCircle2, color: 'text-emerald-400', label: 'Complete' },
  processing: { icon: Loader2, color: 'text-amber-400', label: 'Processing' },
  failed: { icon: XCircle, color: 'text-red-400', label: 'Failed' },
  active: { icon: Circle, color: 'text-blue-400', label: 'Active' },
  abandoned: { icon: Clock, color: 'text-zinc-400', label: 'Expired' },
};

/* ------------------------------------------------------------------ */
/*  Animation variants                                                */
/* ------------------------------------------------------------------ */

const stagger = {
  animate: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

const fadeUp = {
  initial: { opacity: 0, y: 12, scale: 0.98 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  },
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */

const DemoStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { userData } = useAuth();
  const { toast } = useToast();
  const [launching, setLaunching] = React.useState(false);
  const [recordingTitle, setRecordingTitle] = React.useState('');
  const [initialUrl, setInitialUrl] = React.useState('');
  const [seedFiles, setSeedFiles] = React.useState<File[]>([]);
  const [dragOver, setDragOver] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<{ done: number; total: number } | null>(
    null,
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [deleteSegmentsSessionId, setDeleteSegmentsSessionId] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);

  // Reset to first page when switching organizations.
  React.useEffect(() => {
    setPage(1);
  }, [currentOrganization?.id]);

  React.useEffect(() => {
    setRecordingTitle((currentValue) =>
      currentValue.trim() ? currentValue : buildDefaultRecordingName(currentOrganization?.name),
    );
  }, [currentOrganization?.name]);

  /* ---- Queries ---- */

  const sessionsQuery = useQuery({
    queryKey: ['demo-studio-sessions', currentOrganization?.id, page],
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('Organization required');
      const response = await demoStudioService.listSessions(currentOrganization.id, {
        limit: RECORDINGS_PAGE_SIZE,
        offset: (page - 1) * RECORDINGS_PAGE_SIZE,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load demo studio sessions.');
      }
      return response.data;
    },
    enabled: Boolean(currentOrganization?.id),
    refetchInterval: 5000,
    // Keep previous data only when paginating within the same organization.
    // When the org changes, fall back to the skeleton loader instead of
    // briefly rendering the previous org's sessions.
    placeholderData: (previous, previousQuery) => {
      if (previousQuery?.queryKey?.[1] !== currentOrganization?.id) return undefined;
      return previous;
    },
  });

  const segmentsQuery = useQuery({
    queryKey: ['organization-segments', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('Organization required');
      const response = await contentLibraryService.listOrganizationSegments(currentOrganization.id);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load agent asset segments.');
      }
      return response.data;
    },
    enabled: Boolean(currentOrganization?.id),
    refetchInterval: 5000,
  });

  const segmentCountsByVideo = React.useMemo(() => {
    const counts = new Map<string, SegmentLifecycleCounts>();
    for (const segment of segmentsQuery.data?.segments ?? []) {
      const current = counts.get(segment.video_id) || createEmptySegmentCounts();
      current.total += 1;
      if (segment.catalog_status === 'active') current.active += 1;
      if (segment.catalog_status === 'pending') current.staged += 1;
      if (segment.catalog_status === 'library_only') current.library += 1;
      if (segment.catalog_status === 'ignored') current.discard += 1;
      if (segment.catalog_status === 'pending') {
        switch (getRecommendationKind(segment.recommended_action)) {
          case 'add':
            current.suggestedAdd += 1;
            break;
          case 'replacement':
            current.possibleReplacements += 1;
            break;
          case 'duplicate':
            current.possibleDuplicates += 1;
            break;
          case 'library':
            current.suggestedLibrary += 1;
            break;
          case 'discard':
            current.suggestedDiscard += 1;
            break;
          default:
            break;
        }
      }
      counts.set(segment.video_id, current);
    }
    return counts;
  }, [segmentsQuery.data?.segments]);

  /* ---- Mutations ---- */

  const deleteSegmentsMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const session = sessionsQuery.data?.sessions.find((s) => s.id === sessionId);
      if (!session?.organization_video_id || !currentOrganization?.id) {
        throw new Error('Video and organization required');
      }
      // Fetch all segmentations for this video.
      const segRes = await contentLibraryService.listVideoSegmentations(
        session.organization_video_id,
        currentOrganization.id,
      );
      if (!segRes.success || !segRes.data?.segmentations.length) {
        throw new Error('No segmentations found for this video.');
      }
      // Delete segments from every segmentation so nothing is left behind.
      let totalDeleted = 0;
      for (const seg of segRes.data.segmentations) {
        const response = await contentLibraryService.bulkDeleteSegments(
          session.organization_video_id,
          seg.segmentation_id,
          currentOrganization.id,
        );
        if (!response.success || !response.data) {
          throw new Error(response.error || `Failed to delete segments from segmentation ${seg.segmentation_id}.`);
        }
        totalDeleted += response.data.deleted_count;
      }
      return { deleted_count: totalDeleted };
    },
    onSuccess: (data) => {
      toast({
        title: 'Segments deleted',
        description: `${data.deleted_count} segment(s) removed. Catalog updated.`,
      });
      void queryClient.invalidateQueries({
        queryKey: ['demo-studio-sessions', currentOrganization?.id],
      });
      setDeleteSegmentsSessionId(null);
    },
    onError: (error) => {
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Failed to delete segments.',
        variant: 'destructive',
      });
      setDeleteSegmentsSessionId(null);
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await demoStudioService.retryFinalization(sessionId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to retry finalization.');
      }
      return response.data;
    },
    onSuccess: () => {
      toast({ title: 'Retry started', description: 'Finalization is re-running in the background.' });
      void queryClient.invalidateQueries({
        queryKey: ['demo-studio-sessions', currentOrganization?.id],
      });
    },
    onError: (error) => {
      toast({
        title: 'Retry failed',
        description: error instanceof Error ? error.message : 'Failed to retry finalization.',
        variant: 'destructive',
      });
    },
  });

  /* ---- Handlers ---- */

  const handleAddSeedFiles = React.useCallback(
    (files: FileList | File[]) => {
      const MAX_SEED_FILE_BYTES = 25 * 1024 * 1024;
      const incoming = Array.from(files);
      if (incoming.length === 0) return;
      const accepted: File[] = [];
      const rejected: string[] = [];
      for (const file of incoming) {
        if (file.size > MAX_SEED_FILE_BYTES) {
          rejected.push(file.name);
        } else {
          accepted.push(file);
        }
      }
      if (rejected.length > 0) {
        toast({
          title: rejected.length === 1 ? 'File too large' : 'Some files too large',
          description:
            rejected.length === 1
              ? `${rejected[0]} exceeds the 25MB limit.`
              : `These files exceed the 25MB limit: ${rejected.join(', ')}`,
          variant: 'destructive',
        });
      }
      if (accepted.length > 0) {
        setSeedFiles((prev) => [...prev, ...accepted]);
      }
    },
    [toast],
  );

  const handleRemoveSeedFile = React.useCallback((index: number) => {
    setSeedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSeedDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleAddSeedFiles(e.dataTransfer.files);
      }
    },
    [handleAddSeedFiles],
  );

  const handleLaunch = React.useCallback(async () => {
    if (!currentOrganization?.id) {
      toast({ title: 'No organization selected', variant: 'destructive' });
      return;
    }
    if (!recordingTitle.trim()) {
      toast({ title: 'Capture name required', variant: 'destructive' });
      return;
    }
    if (!initialUrl.trim()) {
      toast({ title: 'Starting page required', variant: 'destructive' });
      return;
    }

    setLaunching(true);
    try {
      const response = await demoStudioService.createSession({
        organization_id: currentOrganization.id,
        title: recordingTitle.trim(),
        timezone: userData?.timezone || getBrowserTimezone(),
        initial_url: initialUrl.trim(),
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to launch Demo Studio.');
      }
      const newSessionId = response.data.id;

      let filesAttached = 0;
      if (seedFiles.length > 0) {
        setUploadProgress({ done: 0, total: seedFiles.length });
        for (let i = 0; i < seedFiles.length; i++) {
          const file = seedFiles[i];
          try {
            const uploadResponse = await demoStudioService.uploadFileToBrowser(newSessionId, file);
            if (!uploadResponse.success) {
              throw new Error(uploadResponse.error || `Failed to upload ${file.name}`);
            }
            filesAttached++;
          } catch (uploadError) {
            toast({
              title: `Couldn't attach ${file.name}`,
              description:
                uploadError instanceof Error
                  ? uploadError.message
                  : 'Upload failed — you can still continue capture.',
              variant: 'destructive',
            });
          }
          setUploadProgress({ done: i + 1, total: seedFiles.length });
        }
      }

      if (seedFiles.length > 0 && filesAttached === 0) {
        toast({
          title: 'Studio launched without files',
          description:
            'None of the selected files could be attached. File inputs in the browser won\u2019t have files available.',
          variant: 'destructive',
          duration: 8000,
        });
      } else {
        toast({ title: 'Studio launched', description: 'Browser session is ready.' });
      }
      sessionStorage.setItem(
        `demo-studio-launch:${newSessionId}`,
        JSON.stringify({ title: recordingTitle.trim(), initialUrl: initialUrl.trim() }),
      );
      navigate(`/demo-studio/sessions/${newSessionId}`);
    } catch (error) {
      toast({
        title: 'Launch failed',
        description: error instanceof Error ? error.message : 'Failed to launch.',
        variant: 'destructive',
      });
    } finally {
      setLaunching(false);
      setUploadProgress(null);
    }
  }, [currentOrganization?.id, initialUrl, navigate, recordingTitle, seedFiles, toast, userData?.timezone]);

  const recentSessions = sessionsQuery.data?.sessions ?? [];
  const totalSessions = sessionsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalSessions / RECORDINGS_PAGE_SIZE));

  // Keep current page in bounds (e.g. after captures are deleted).
  React.useEffect(() => {
    if (sessionsQuery.data && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, sessionsQuery.data, totalPages]);
  const canLaunch =
    Boolean(currentOrganization?.id) &&
    recordingTitle.trim().length > 0 &&
    initialUrl.trim().length > 0;

  /* ---- Render ---- */

  return (
    <>
      <Helmet>
        <title>Studio | Hobbes</title>
      </Helmet>

      <div className="flex min-h-0 flex-col">
        <PageHeader
          title="Product Studio"
          titleTooltip="Record product walkthroughs in a managed browser."
        />

        <motion.div
          className="flex-1 px-4 py-6 sm:px-6 md:px-8"
          variants={stagger}
          initial="initial"
          animate="animate"
        >
          {/* ---- Launch Card ---- */}
          <motion.div variants={fadeUp}>
            <Card className="border card-border shadow-none overflow-hidden">
              <CardContent className="p-0">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto]">
                  {/* Left: form */}
                  <div className="p-5 space-y-4">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <MonitorPlay className="h-4 w-4" />
                      </div>
                      <h2 className="text-sm font-medium text-foreground">New capture</h2>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="demo-studio-recording-title" className="text-xs">
                          Capture name
                        </Label>
                        <Input
                          id="demo-studio-recording-title"
                          type="text"
                          placeholder="Q2 product overview"
                          value={recordingTitle}
                          onChange={(e) => setRecordingTitle(e.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="demo-studio-initial-url" className="text-xs">
                          Starting page
                        </Label>
                        <Input
                          id="demo-studio-initial-url"
                          type="url"
                          placeholder="https://app.example.com/login"
                          value={initialUrl}
                          onChange={(e) => setInitialUrl(e.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                          className="h-9"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">
                          Attachments{' '}
                          <span className="font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <HelpTooltip text="Files added here are available in the browser session — pick one when a file picker opens during capture." />
                      </div>
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleSeedDrop}
                        className={`flex items-center gap-3 rounded-md border border-dashed px-3 py-2.5 transition ${
                          dragOver
                            ? 'border-primary/60 bg-primary/5'
                            : 'border-border hover:border-foreground/30'
                        }`}
                      >
                        <Upload className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        <span className="flex-1 text-xs text-muted-foreground">
                          Drag files here or browse
                        </span>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={launching}
                          className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
                        >
                          Browse
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files) {
                              handleAddSeedFiles(e.target.files);
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>
                      {seedFiles.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {seedFiles.map((file, index) => (
                            <span
                              key={`${file.name}-${index}`}
                              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/50 py-0.5 pl-2 pr-1 text-xs text-foreground"
                            >
                              <FileIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                              <span className="max-w-[180px] truncate">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveSeedFile(index)}
                                disabled={launching}
                                aria-label={`Remove ${file.name}`}
                                className="rounded p-0.5 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-50"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: launch */}
                  <div className="flex items-center border-t lg:border-t-0 lg:border-l border-border/60 bg-muted/20 px-8 py-5">
                    <motion.div
                      whileHover={canLaunch && !launching ? { scale: 1.02 } : undefined}
                      whileTap={canLaunch && !launching ? { scale: 0.97 } : undefined}
                    >
                      <Button
                        size="lg"
                        onClick={handleLaunch}
                        disabled={launching || !canLaunch}
                        className="gap-2 px-6 h-10"
                      >
                        {launching ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {uploadProgress
                              ? `Uploading files (${uploadProgress.done}/${uploadProgress.total})…`
                              : 'Launching…'}
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4" />
                            Launch Studio
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* ---- Capture ---- */}
          <motion.div variants={fadeUp} className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-medium text-foreground">Capture</h2>
                {sessionsQuery.isFetching && (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                )}
              </div>
              {totalSessions > 0 && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {totalSessions > RECORDINGS_PAGE_SIZE ? (
                    <>
                      {(page - 1) * RECORDINGS_PAGE_SIZE + 1}
                      {'–'}
                      {Math.min(page * RECORDINGS_PAGE_SIZE, totalSessions)} of {totalSessions}
                    </>
                  ) : (
                    <>
                      {totalSessions} capture{totalSessions !== 1 ? 's' : ''}
                    </>
                  )}
                </span>
              )}
            </div>

            {sessionsQuery.isLoading ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-[128px] rounded-xl border border-border/50 bg-muted/20 animate-pulse"
                  />
                ))}
              </div>
            ) : recentSessions.length === 0 ? (
              <Card className="border border-dashed card-border shadow-none">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/50 mb-3">
                    <Video className="h-5 w-5 text-muted-foreground/60" />
                  </div>
                  <p className="text-sm text-muted-foreground">No captures yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="overflow-x-auto rounded-[8px] border card-border bg-background">
                <AnimatePresence mode="popLayout">
                  {recentSessions.map((session, idx) => {
                    const hasVideo = Boolean(session.organization_video_id);
                    const segmentCountsLoaded = Boolean(segmentsQuery.data);
                    const segmentCountsLoading =
                      hasVideo && !segmentCountsLoaded && (segmentsQuery.isLoading || segmentsQuery.isFetching);
                    const segmentCounts = session.organization_video_id
                      ? segmentCountsByVideo.get(session.organization_video_id)
                      : undefined;
                    const lifecycleCounts = segmentCounts || createEmptySegmentCounts();
                    const stagedCount = segmentCounts?.staged ?? 0;
                    const canReconnect =
                      ['active', 'launching', 'abandoned'].includes(session.status) && !hasVideo;
                    const canOpenAssets = Boolean(session.organization_video_id);
                    const cfg = STATUS_CONFIG[session.status] ?? {
                      icon: Circle,
                      color: 'text-muted-foreground',
                      label: session.status,
                    };
                    const hasGeneratedAssets = lifecycleCounts.total > 0;
                    const isAssetPending = hasVideo && segmentCountsLoaded && !hasGeneratedAssets;
                    const isReadyToReview = hasVideo && segmentCountsLoaded && stagedCount > 0;
                    const isReviewed = hasVideo && segmentCountsLoaded && hasGeneratedAssets && stagedCount === 0;
                    const statusLabel = segmentCountsLoading
                      ? 'Checking agent assets'
                      : isReadyToReview
                        ? 'Ready to review'
                        : isReviewed
                          ? 'Reviewed'
                          : isAssetPending
                            ? 'Agent assets pending'
                            : cfg.label;
                    const statusColor = segmentCountsLoading
                      ? 'text-muted-foreground'
                      : isReadyToReview
                        ? 'text-amber-400'
                        : isReviewed
                          ? 'text-emerald-400'
                          : isAssetPending
                            ? 'text-muted-foreground'
                            : cfg.color;
                    const StatusIcon = segmentCountsLoading
                      ? Loader2
                      : isReadyToReview
                        ? ListChecks
                        : isReviewed
                          ? CheckCircle2
                          : isAssetPending
                            ? Clock
                            : cfg.icon;
                    const assetSummary = segmentCountsLoading
                      ? 'Loading segment states'
                      : isReadyToReview
                        ? formatReadySummary(lifecycleCounts)
                        : isReviewed
                          ? formatReviewedSummary(lifecycleCounts)
                          : hasVideo
                            ? 'Agent assets pending'
                            : 'No agent assets yet';
                    const openSession = () => {
                      if (canOpenAssets) {
                        window.location.assign(
                          `/content-library?tab=${isReadyToReview ? 'staged' : 'all'}&source=${
                            session.organization_video_id
                          }`,
                        );
                      } else if (canReconnect) {
                        navigate(`/demo-studio/sessions/${session.id}`);
                      }
                    };

                    return (
                      <motion.div
                        key={session.id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: 0.24,
                          delay: idx * 0.025,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        role={canReconnect || canOpenAssets ? 'button' : undefined}
                        tabIndex={canReconnect || canOpenAssets ? 0 : undefined}
                        onClick={canReconnect || canOpenAssets ? openSession : undefined}
                        onKeyDown={(event) => {
                          if ((event.key === 'Enter' || event.key === ' ') && (canReconnect || canOpenAssets)) {
                            event.preventDefault();
                            openSession();
                          }
                        }}
                        className={`group grid gap-3 border-b border-border/50 px-4 py-3 transition-colors last:border-b-0 md:min-w-[1080px] md:grid-cols-[minmax(280px,320px)_minmax(280px,1fr)_170px_320px] md:items-center ${
                          canReconnect || canOpenAssets ? 'cursor-pointer hover:bg-muted/30' : ''
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="truncate text-sm font-medium text-foreground">
                              {session.title || 'Untitled capture'}
                            </h3>
                            {session.linked_knowledge_gap_summary_id && (
                              <Badge
                                variant="outline"
                                className="h-5 cursor-pointer gap-1 whitespace-nowrap px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  navigate(
                                    `/agent-knowledge?tab=gaps&selected=${encodeURIComponent(session.linked_knowledge_gap_summary_id)}`,
                                  );
                                }}
                                title="Open the linked knowledge gap"
                              >
                                From knowledge gap
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <StatusIcon
                              className={`h-3 w-3 ${statusColor} ${segmentCountsLoading || session.status === 'processing' ? 'animate-spin' : ''}`}
                            />
                            <span className="text-xs text-muted-foreground">{statusLabel}</span>
                          </div>
                        </div>

                        <div className="min-w-0 space-y-1 md:px-2">
                          {hasVideo ? (
                            <>
                              <Badge
                                variant={isReadyToReview ? 'default' : 'secondary'}
                                className="h-6 gap-1 px-2 text-[11px]"
                              >
                                {isReadyToReview ? (
                                  <ListChecks className="h-3 w-3" />
                                ) : segmentCountsLoading ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="h-3 w-3" />
                                )}
                                {isReadyToReview
                                  ? formatCount(stagedCount, 'staged segment')
                                  : segmentCountsLoading
                                    ? 'Checking'
                                    : formatCount(lifecycleCounts.total, 'segment')}
                              </Badge>
                              <p className="truncate text-xs text-muted-foreground">
                                {assetSummary}
                              </p>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">{assetSummary}</span>
                          )}
                        </div>

                        <span className="text-xs tabular-nums text-muted-foreground md:justify-self-start">
                          {formatSessionTime(session.updated_at)}
                        </span>

                        <div className="flex min-w-[300px] items-center justify-end gap-2 md:sticky md:right-0 md:z-10 md:self-stretch md:bg-background md:pl-4 md:justify-self-end md:group-hover:bg-muted/30">
                          {canOpenAssets && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 shrink-0 gap-1.5 px-2 text-xs text-muted-foreground"
                              onClick={(event) => {
                                event.stopPropagation();
                                openSession();
                              }}
                            >
                              <Clapperboard className="h-3 w-3" />
                              {isReadyToReview ? 'Review in Agent Assets' : 'View segments'}
                            </Button>
                          )}
                          {hasVideo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 shrink-0 gap-1.5 px-2 text-xs text-red-500 hover:text-red-600"
                              disabled={deleteSegmentsMutation.isPending && deleteSegmentsMutation.variables === session.id}
                              onClick={(event) => {
                                event.stopPropagation();
                                setDeleteSegmentsSessionId(session.id);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                              Delete segments
                            </Button>
                          )}
                          {(session.status === 'failed' ||
                            (session.status === 'complete' && !session.organization_video_id)) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 shrink-0 gap-1.5 px-2 text-xs text-amber-500 hover:text-amber-600"
                              disabled={retryMutation.isPending && retryMutation.variables === session.id}
                              onClick={(event) => {
                                event.stopPropagation();
                                retryMutation.mutate(session.id);
                              }}
                            >
                              <RotateCw className={`h-3 w-3 ${retryMutation.isPending && retryMutation.variables === session.id ? 'animate-spin' : ''}`} />
                              Retry
                            </Button>
                          )}
                          {(canReconnect || canOpenAssets) && (
                            <ArrowRight className="h-3 w-3 text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100" />
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}

            {totalSessions > RECORDINGS_PAGE_SIZE && (
              <div className="flex items-center justify-between pt-4">
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1"
                    disabled={page <= 1 || sessionsQuery.isFetching}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1"
                    disabled={page >= totalPages || sessionsQuery.isFetching}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* ---- Delete Segments Confirmation ---- */}
      <AlertDialog
        open={Boolean(deleteSegmentsSessionId)}
        onOpenChange={(open) => !open && setDeleteSegmentsSessionId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all segments?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove all segments from this video and update the catalog.
              The video itself will be kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteSegmentsMutation.isPending}
              onClick={() => {
                if (deleteSegmentsSessionId) {
                  deleteSegmentsMutation.mutate(deleteSegmentsSessionId);
                }
              }}
            >
              {deleteSegmentsMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete all segments'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </>
  );
};

export default DemoStudioPage;
